import React, { useMemo, ReactNode } from "react";
import { Col, Row, Tooltip, Checkbox, Divider, Spin } from "antd";
import moment from "moment";
import { Gutter } from "antd/lib/grid/row";
import { useAppSelector } from "../../..";
import { getFio, toDisplayFormat, geDicValueById, getDictionaryItemById } from "../../../lib/utils";
import { ReactComponent as EditIcon } from "../../../img/versions_icons/editVersion_icon.svg";
import { ReactComponent as AddVersionIcon } from "../../../img/versions_icons/addVersion_icon.svg";
import { ReactComponent as CloseVersionIcon } from "../../../img/versions_icons/closeVersion_icon.svg";
import type { AgreementShort, IChangingMode, Offer } from "../../../lib/types";
import { OFFER_STATUSES } from "../../../store/slices/offering/const";
import { MENU_ADMIN_OFFERING_WRITE } from "../../../lib/const/permissions";
import { hasAccess } from "../../RolesModule/hasAccess";
import { IChangingRecordKeyNumber } from "../../../lib/types/IViewMode";
import "./OfferParamsView.less";

const rowLayout = {
  gutter: 50,
  style: {
    marginTop: "10px",
  },
};

interface ComponentProps {
  changeViewMode?: (params?: IChangingRecordKeyNumber) => void;
  idx?: number;
  isChild?: boolean;
  offer: Offer;
  isLoading?: boolean;
  isLatestVersion?: boolean;
  isEditingDisabled?: boolean;
}

/* ------------------------------------------------------ Начало компонента ------------------------------------------------------ */

export const OfferParamsView = (props: ComponentProps) => {
  const {
    changeViewMode,
    idx,
    offer,
    isLoading = false,
    isChild = false,
    isLatestVersion,
    isEditingDisabled,
  } = props;
  const {
    offeringAccessTypes,
    offeringStatuses,
    offeringProlongTypes,
    lifespanPeriods,
    offeringBundleTypes,
  } = useAppSelector((state) => state.dicts);

  const {
    tariffPeriodAlignments,
    tariffPeriodAlignmentsLoading,
    tariffModelTypes,
    tariffModelTypesLoading,
  } = useAppSelector((state) => state.rtkDictionaries);
  const { serviceTypeList } = useAppSelector((state) => state.offering);

  const isCurrentVersion = useMemo(() => {
    const fromDate = offer?.fromDate && moment(offer.fromDate);
    const toDate = offer?.toDate && moment(offer.toDate);
    if (fromDate && toDate) {
      return moment().isBetween(fromDate, toDate);
    }
    return false;
  }, [offer]);

  const isPastVersion = useMemo(() => {
    const fromDate = offer?.fromDate && moment(offer.fromDate);
    const toDate = offer?.toDate && moment(offer.toDate);
    if (fromDate && toDate) {
      return toDate.isBefore(moment());
    }
    return false;
  }, [offer]);

  const agreementTitle = useMemo((): ReactNode => {
    if (offer?.agreement) {
      const { id, account, agreementNumber } = offer.agreement;
      const fio = account?.individual && getFio(account.individual);
      const fullName = account?.organization?.fullName;
      const agreementData = [id, agreementNumber, fullName || fio].filter((el) => Boolean(el));
      return agreementData.map((item, i) => <span key={i}>{item}</span>);
    }
    return null;
  }, [offer?.agreement]);

  const renderDataItem = (props: {
    label: string;
    data?: ReactNode;
    offsetCol?: number;
    spanCol?: number;
    labelCol?: number;
    wrapperCol?: number;
    styleLabel?: React.CSSProperties;
    styleLabelCol?: React.CSSProperties;
    styleData?: React.CSSProperties;
    styleDataCol?: React.CSSProperties;
    rowGutter?: Gutter;
    tooltipTitle?: string;
    tooltipData?: string;
  }): ReactNode => {
    const {
      label,
      data,
      offsetCol = 0,
      spanCol = 6,
      labelCol = 10,
      wrapperCol = 14,
      styleLabel,
      styleLabelCol,
      styleData,
      styleDataCol,
      rowGutter,
      tooltipTitle = "",
      tooltipData = "",
    } = props;
    return (
      <Col offset={offsetCol} span={spanCol}>
        <Row gutter={rowGutter} className="offer-params-view__dataRow">
          <Col span={labelCol} style={styleLabelCol}>
            <Tooltip placement="topLeft" title={tooltipTitle}>
              <label style={styleLabel}>{label}</label>
            </Tooltip>
          </Col>
          <Col span={wrapperCol} style={styleDataCol}>
            <Tooltip placement="topLeft" title={tooltipData}>
              <span style={styleData}>{data}</span>
            </Tooltip>
          </Col>
        </Row>
      </Col>
    );
  };

  const handleSetViewMode = (
    e: React.MouseEvent<SVGSVGElement, MouseEvent>,
    mode: IChangingMode
  ) => {
    e.stopPropagation();
    changeViewMode && idx !== undefined && changeViewMode([idx, mode]);
  };

  const renderButtons = () =>
    hasAccess(MENU_ADMIN_OFFERING_WRITE) && isChild && !isEditingDisabled ? (
      <Row gutter={7} justify="end" className="offer-params-view__btns">
        {isLatestVersion && offer?.offeringStatusId === OFFER_STATUSES.ARCHIVE && (
          <Col>
            <CloseVersionIcon
              onClick={(e) => handleSetViewMode(e, "close")}
              className="offer-params-view__iconBtn"
            />
          </Col>
        )}
        {!isPastVersion && (
          <>
            <Col>
              <Tooltip title="Добавить версию">
                <AddVersionIcon
                  onClick={(e) => handleSetViewMode(e, "add")}
                  className="offer-params-view__iconBtn"
                />
              </Tooltip>
            </Col>
            <Col>
              <Tooltip title="Редактировать">
                <EditIcon
                  onClick={(e) => handleSetViewMode(e, "edit")}
                  className="offer-params-view__iconBtn"
                />
              </Tooltip>
            </Col>
          </>
        )}
      </Row>
    ) : null;

  const childClassName = useMemo(() => {
    if (!isChild) return "";
    if (isCurrentVersion) return `row_child row_current-version`;
    if (isPastVersion) return "row_child row_past-version";
    return "row_child";
  }, [offer, isChild]);

  return (
    <div className={`offer-params-view ${childClassName}`}>
      <Divider style={{ margin: "0 0 9px" }} />
      <Spin spinning={isLoading}>
        <Row {...rowLayout}>
          {renderDataItem({
            label: "Наименование",
            data: offer?.extName,
            tooltipData: offer?.extName,
            styleDataCol: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
          })}
          {renderDataItem({
            label: "Тип пролонгации",
            data: getDictionaryItemById(offeringProlongTypes, offer?.prolongTypeId)?.name,
          })}
          {renderDataItem({
            label: "Тип",
            data: getDictionaryItemById(offeringAccessTypes, offer?.offeringTypeId)?.name,
          })}
          {renderDataItem({
            label: "Статус",
            data: (
              <Row>
                <Col span={11}>
                  {getDictionaryItemById(offeringStatuses, offer?.offeringStatusId)?.name}
                </Col>
                <Col span={13}>{renderButtons()}</Col>
              </Row>
            ),
          })}
        </Row>
        <Row {...rowLayout}>
          {renderDataItem({
            label: "Код",
            data: offer?.name,
            tooltipData: offer?.name,
            styleDataCol: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
          })}
          <Col span={6}>
            <Row>
              {renderDataItem({
                label: "Период продукта",
                data: getDictionaryItemById(lifespanPeriods, offer?.lifespanPeriodId)?.name,
                spanCol: 19,
                labelCol: 13,
                wrapperCol: 11,
              })}
              {renderDataItem({
                label: "Кол-во",
                data: offer?.lifespanPeriodQty,
                spanCol: 5,
                labelCol: 14,
                wrapperCol: 10,
                rowGutter: 5,
              })}
            </Row>
          </Col>
          <Tooltip
            title={agreementTitle}
            overlayClassName="offer-params-view__agreementData"
            placement="bottom"
          >
            {renderDataItem({
              label: "Договор",
              data: offer?.agreement?.agreementNumber || offer?.agreement?.id || offer?.agreementId,
            })}
          </Tooltip>
          {renderDataItem({
            label: "Доступ к подкл.",
            data: <Checkbox disabled checked={offer?.isStandalone} />,
          })}
        </Row>
        <Row {...rowLayout}>
          {renderDataItem({
            label: "Тип пакета",
            data: geDicValueById(offeringBundleTypes, offer?.bundleTypeId)?.name,
          })}
          {renderDataItem({
            label: "Смещение периода",
            data: geDicValueById(tariffPeriodAlignments, offer?.periodAlignmentId)?.name,
          })}
          {renderDataItem({
            label: "Период с",
            data: offer?.fromDate && toDisplayFormat(offer.fromDate),
            wrapperCol: 9,
            offsetCol: 6,
          })}
        </Row>
        <Row {...rowLayout}>
          {renderDataItem({
            label: "Бизнес-модель",
            data: geDicValueById(tariffModelTypes, offer?.tariffModelId)?.name,
            offsetCol: 6,
          })}
          {renderDataItem({
            label: "Период по",
            data: offer?.toDate && toDisplayFormat(offer.toDate),
            wrapperCol: 9,
            offsetCol: 6,
          })}
        </Row>
      </Spin>
    </div>
  );
};
